import * as focusTrap from 'focus-trap';
import CartItem from '../components/cart/CartItem';
import GiftWithPurchase from '../components/cart/GiftWithPurchase';
import ProductItem from '../components/product/ProductItem';
import initCarousel from '../helpers/carousel';
import { disablePageScroll, enablePageScroll } from '@fluejs/noscroll';

const selectors = {
  minicart: '[data-minicart]',
  drawer: '[data-minicart-drawer]',
  cartIcon: '[data-cart-icon]',
  backdrop: '[data-minicart-backdrop]',
  close: '[data-cart-close]',
  cartItem: '[data-cart-item]',
  duplicateSizeNotification: '[data-duplicate-size-notification]',
  duplicateSizeNotificationClose: '[data-duplicate-size-notification-close]',
  cartUpsells: '[data-cart-upsells]',
};

const OPEN_PARAM = 'openMiniCart';

export default class MainMinicart {
  constructor(section) {
    this.el = section.el;
    this.data = section.data;
    const freeGiftVariant = this.data.freeGiftVariant;
    this.freeGiftVariantId =
      freeGiftVariant && typeof freeGiftVariant === 'object'
        ? freeGiftVariant.id
        : freeGiftVariant;
    this.storageKey = 'lof-free-gift-removal-' + this.freeGiftVariantId;

    this.cartUpsellSplide = null;
    this._init();

    this.focusTrap = focusTrap.createFocusTrap(selectors.minicart, {
      clickOutsideDeactivates: true,
    });

    const url = new URL(window.location.href);
    if (url.searchParams.has(OPEN_PARAM)) {
      this.openMinicart();
      this.removeOpenParam();
    }
  }

  _init = () => {
    this.headerCartBubble = document.querySelector(selectors.headerCartBubble);
    this.cartIcons = document.querySelectorAll(selectors.cartIcon);

    if (window.location.pathname !== '/cart') {
      this.cartIcons.forEach(icon => {
        icon.addEventListener('click', this.openMinicart);
      });
    }

    this.minicart = this.el.querySelector(selectors.minicart);
    this.drawer = this.el.querySelector(selectors.drawer);

    document.addEventListener('updateCart', res => this.updateCart(res.detail));

    this._attachListeners();

    if (this.freeGiftVariantId) {
      this.checkCartForFreeGift();
    }

    if (this.data.gwp) {
      this.giftWithPurchase = new GiftWithPurchase({
        getRoot: () => this.drawer?.querySelector('[data-cart-gwp]'),
        config: this.data.gwp,
      });
    }
  };

  _attachListeners = () => {
    const close = this.el.querySelector(selectors.close);
    close.addEventListener('click', this.closeMinicart);

    const backdrop = this.el.querySelector(selectors.backdrop);
    backdrop.addEventListener('click', this.closeMinicart);

    const cartItems = this.drawer.querySelectorAll(selectors.cartItem);
    cartItems.forEach(item => {
      new CartItem(item, this.updateCart);
    });

    const cartUpsells = this.drawer.querySelector(selectors.cartUpsells);

    if (cartUpsells) {
      this.cartUpsellSplide = initCarousel(
        cartUpsells,
        {
          perPage: 1,
          gap: '0.5rem',
          padding: { right: '15%' },
        },
        true,
      );

      const productItems = cartUpsells.querySelectorAll('[data-product-item]');
      productItems.forEach(productItem => new ProductItem(productItem));
    }

    const duplicateSizeNotification = this.el.querySelector(
      selectors.duplicateSizeNotification,
    );
    if (duplicateSizeNotification) {
      duplicateSizeNotification.addEventListener(
        'click',
        this.closeDuplicateSizeNotification,
      );
    }
  };

  // Both guards keep the noscroll lock balanced. disablePageScroll/enablePageScroll
  // are reference counted, so a second open while already open (updateCart passes
  // openCart: true for every add, including cart upsells) would take a second lock
  // that the single close on the way out never releases, leaving the page stuck at
  // overflow: hidden. `is-open` is the only state these two methods share, and
  // nothing else toggles it.
  openMinicart = () => {
    if (this.minicart.classList.contains('is-open')) {
      return;
    }

    this.minicart.classList.add('is-open');
    disablePageScroll();
    this.focusTrap.activate();
  };

  closeMinicart = () => {
    if (!this.minicart.classList.contains('is-open')) {
      return;
    }

    this.minicart.classList.remove('is-open');
    enablePageScroll();
    this.focusTrap.deactivate();
  };

  updateCart = res => {
    if (this.cartUpsellSplide) {
      this.cartUpsellSplide.destroy();
      this.cartUpsellSplide = null;
    }

    const responseText = res.sections['main-minicart'];
    const mainHtml = new DOMParser().parseFromString(responseText, 'text/html');
    this.drawer.innerHTML = mainHtml.querySelector(selectors.drawer).innerHTML;

    const cartTrigger = res.sections['main-cart-trigger'];
    const triggerHtml = new DOMParser().parseFromString(
      cartTrigger,
      'text/html',
    );

    this.cartIcons.forEach(icon => {
      icon.innerHTML = triggerHtml.querySelector(selectors.cartIcon).innerHTML;
    });

    this._attachListeners();

    this.giftWithPurchase?.sync();

    if (res.openCart) {
      this.openMinicart();
    }
  };

  closeDuplicateSizeNotification = () => {
    const duplicateSizeNotification = this.el.querySelector(
      selectors.duplicateSizeNotification,
    );
    if (duplicateSizeNotification) {
      duplicateSizeNotification.remove();
    }
  };

  removeOpenParam = () => {
    const paramExp = new RegExp(`[\?&]${OPEN_PARAM}+`);
    history.replaceState(
      null,
      '',
      location.pathname +
        location.search.replace(paramExp, '').replace(/^&/, '?') +
        location.hash,
    );
  };

  checkCartForFreeGift = () => {
    // Market-scoped, like every other cart call (see helpers/cartApi). An unprefixed `/cart.js` is
    // answered on the primary market — on `/en-ca/` it returns USD and rewrites the session's
    // `cart_currency` cookie to USD, which is not something a read of the cart should ever do.
    fetch(`${Shopify.routes.root}cart.js`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Unable to load cart');
        }

        return response.json();
      })
      .then(cart => {
        const matchingItems = (cart.items || []).filter(item => {
          return Number(item.variant_id) === Number(this.freeGiftVariantId);
        });

        if (!matchingItems.length) {
          sessionStorage.removeItem(this.storageKey);
          return;
        }

        removeFreeGiftFromCart(matchingItems);
      })
      .catch(error => {
        console.error('[lof free gift removal]', error);
        sessionStorage.removeItem(this.storageKey);
      });
  };

  removeFreeGiftFromCart = matchingItems => {
    if (!matchingItems || !matchingItems.length) {
      sessionStorage.removeItem(this.storageKey);
      return;
    }

    sessionStorage.setItem(this.storageKey, 'pending');

    Promise.all(
      matchingItems.map(item => {
        return fetch(`${Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: item.key,
            quantity: 0,
          }),
        }).then(response => {
          if (!response.ok) {
            throw new Error('Unable to remove free gift line item');
          }

          return response.json();
        });
      }),
    )
      .then(() => {
        sessionStorage.setItem(this.storageKey, 'done');
        window.location.reload();
      })
      .catch(error => {
        console.error('[lof free gift removal]', error);
        sessionStorage.removeItem(this.storageKey);
      });
  };
}
